import { Helmet } from 'react-helmet-async';
import { AgGridReact } from 'ag-grid-react';
import { useState, useEffect, useRef, useMemo } from 'react';
import { enqueueSnackbar } from 'notistack';
import { Autocomplete, Box, CircularProgress, TextField, Typography, IconButton, Button, Tooltip, InputAdornment, Stack } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { DeleteOutline } from '@mui/icons-material';
import RestoreIcon from '@mui/icons-material/Restore';
import CurrencyInput from 'react-currency-input-field';
import axiosInstance from '../../utils/axios';
import Iconify from '../../reuseable-components/iconify/Iconify';
import AGGridQuickFilterWrapper from '../../reuseable-components/AGGrid/AGGridQuickFilterWrapper';
import AGGridPictureCol from '../../reuseable-components/AGGrid/AGGridPictureCol';
import { AGGridDeleteCol, DeleteStartButton, DeleteSubmitButton } from '../../reuseable-components/AGGrid/AGGridDeleteCol';
import autoSizeColumns from '../../reuseable-components/AGGrid/AGGridAutoSizeCols';
import EditIconButton from '../../reuseable-components/DefaultButtons/EditIconButton';
import SubmitOrderSummary from './SubmitOrderSummary';
import AgGridDefaults from '../../reuseable-components/AGGrid/AGGridDefaults';
import { objectToFormData } from '../../utils/objectToFormData';

export default function CartTable() {
  const gridRefs = useRef({});

  const [products, setProducts] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [selectedFacility, setSelectedFacility] = useState(null);
  const [facilityAddress, setFacilityAddress] = useState(null);
  const [canEdit, setCanEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [startSubmittingOrder, setStartSubmittingOrder] = useState(false);
  const [startDeletingItems, setStartDeletingItems] = useState(false);
  const [formData, setFormData] = useState([]);
  const [formDataPerCart, setFormDataPerCart] = useState([]);
  const [visibleGrids, setVisibleGrids] = useState({});
  const [pendingEdits, setPendingEdits] = useState({});
  const [editingRow, setEditingRow] = useState({});
  const [modifiedFieldsMap, setModifiedFieldsMap] = useState({});
  const [isChanges, setIsChanges] = useState({});

  const pendingEditsRef = useRef({});
  useEffect(() => {
    pendingEditsRef.current = pendingEdits;
  }, [pendingEdits]);

  const showVendorCols = useMemo(() => products?.some(product => product.vendor) ?? false, [products])

  useEffect(() => {
    axiosInstance.get(`/cart/cartData`)
      .then(res => {
        setProducts(res.data.items ?? []);
        setFormData(res.data.items ?? []);
        setFacilities(res.data.facilities);
        setFacilityAddress(res.data.address);
        setSelectedFacility(res.data.facilities.find(facility => facility.value === res.data.facilityId));
        setCanEdit(res.data.canEdit);
      })
      .catch(() => enqueueSnackbar('Cart list failed', { variant: 'error' }))
      .finally(() => setLoading(false));
  }, []);

  const fetchData = (facilityId) => {
    if (facilityId) {
      setLoading(true);
      axiosInstance.get(`/cart/cartItems?facilityId=${facilityId}`)
        .then(res => {
          setProducts(res.data.items);
          setCanEdit(res.data.canEdit);
          setFacilityAddress(res.data.address);
          setFormData((res.data.items ?? []).map(item => ({ quantity: item.quantity, price: item.price, originalPrice: item.originalPrice, id: item.id, purchaseOrderNumber: item.purchaseOrderNumber, vendor: item.vendor })));
        })
        .catch(() => enqueueSnackbar('Cart list failed', { variant: 'error' }))
        .finally(() => setLoading(false));
    } else setProducts([]);
  }

  const updateCart = (poNumber) => {
    const originalItems = pendingEditsRef.current?.[poNumber] || [];
    const updatedItems = originalItems.map(item => {
      const modified = modifiedFieldsMap?.[item.id] ?? [];
      return {
        ...item, price: modified.includes('price') ? item.price : 0, quantity: modified.includes('quantity') ? item.quantity : 0,
        vendor: { ...item.vendor, vendorPrice: modified.includes('vendorPrice') ? item.vendor?.vendorPrice : 0, }
      };
    });
    axiosInstance.post(`/cart/updateCart`, objectToFormData({ facilityId: selectedFacility?.value, purchaseOrderNumber: poNumber, items: updatedItems, }))
      .then((res) => {
        if (res.data) {
          enqueueSnackbar('Cart updated', { variant: 'success' });
          setFormData(prev => {
            const filtered = prev.filter(item => item.purchaseOrderNumber !== poNumber);
            const updated = products.filter(item => item.purchaseOrderNumber === poNumber);
            return [...filtered, ...updated];
          });
        } else enqueueSnackbar('Update cart failed', { variant: 'error' });
      })
      .catch(() => enqueueSnackbar('Update cart failed', { variant: 'error' }))
      .finally(() => {
        setPendingEdits(prev => { const copy = { ...prev }; delete copy[poNumber]; return copy; });
        setModifiedFieldsMap(prev => {
          const updated = { ...prev };
          products.filter(item => item.purchaseOrderNumber === poNumber).forEach(item => delete updated[item.id]); return updated;
        });
      });
  };

  const trackModifiedField = (id, field, isModified) => {
    setModifiedFieldsMap(prev => {
      const fields = new Set(prev[id] ?? []);
      if (isModified) fields.add(field);
      else fields.delete(field);
      return { ...prev, [id]: Array.from(fields) };
    });
  };

  const updateLocally = (params, field, value) => {
    const newNodeData = { ...params.node.data };
    newNodeData[field] = value
    params.node.setData(newNodeData);
  };

  const updateField = (params, field, value) => {
    const newValue = Number(value);
    const original = formData.find(item => item.id === params.data.id);
    const updatedItem = { ...params.node.data };
    let isModified = false;
    if (field.includes('.')) {
      const [parentKey, childKey] = field.split('.');
      const originalValue = original?.[parentKey]?.[childKey];
      isModified = originalValue !== newValue;
      updatedItem[parentKey] = { ...params.data[parentKey], [childKey]: newValue };
      field = childKey;
    }
    else {
      isModified = original?.[field] !== newValue;
      updatedItem[field] = newValue;
    }
    const newFieldKey = `new${field.charAt(0).toUpperCase() + field.slice(1)}`;
    trackModifiedField(params.data.id, field, isModified);
    setProducts(prev => prev.map(item => item.id === params.data.id ? updatedItem : item));
    setPendingEdits(prev => ({
      ...prev, [params.node.data.purchaseOrderNumber]: [...(prev[params.node.data.purchaseOrderNumber] ?? [])
        .filter(item => item.id !== params.data.id), updatedItem]
    }));
    setIsChanges(prev => ({ ...prev, [params.node.data.purchaseOrderNumber]: false }));
  };

  const deleteCart = (poNumber) => {
    axiosInstance.post(`/cart/deleteCart?PONumber=${poNumber}`)
      .then((res) => {
        if (res.data) {
          enqueueSnackbar("Cart deleted", { variant: "success" });
          setFormData(formData => formData.filter(x => x.purchaseOrderNumber !== poNumber))
          setProducts(products => products.filter(x => x.purchaseOrderNumber !== poNumber));
        }
        else enqueueSnackbar('Failed to delete cart', { variant: 'error' });
      })
      .catch(() => enqueueSnackbar("Failed to delete cart", { variant: "error" }))
  }

  const groupedProducts = useMemo(() => {
    const groups = {};
    products.forEach((item) => {
      const po = item.purchaseOrderNumber ?? 'Unknown PO';
      if (!groups[po]) groups[po] = [];
      groups[po].push(item);
    });
    return groups;
  }, [products]);

  Object.keys(groupedProducts).forEach(po => {
    if (!gridRefs.current[po]) {
      gridRefs.current[po] = { current: null };
    }
  });

  const handleFilterChanged = (poNumber, api) => {
    const visibleRowCount = api.getDisplayedRowCount();
    setVisibleGrids(prev => ({ ...prev, [poNumber]: visibleRowCount > 0 }));
  };

  const columns = useMemo(() => [
    AGGridPictureCol(),
    {
      field: 'productName',
      width: 150,
      flex: 1
    },
    {
      field: 'productId',
      headerName: 'SKU',
    },
    {
      field: 'uomType',
      headerName: 'UOM Type',
    },
    ...(showVendorCols ? [
      {
        field: 'vendor.vendor',
        headerName: 'Vendor',
      },
      {
        field: 'vendor.vendorPrice',
        headerName: 'Vendor Price',
        minWidth: 160,
        cellRenderer: (params) => (
          <Stack direction="row" spacing={1} alignItems="center"> {
            editingRow[params.data.id] && canEdit
              ? <> <CurrencyInput name="vendorPrice" sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, width: "100px" }} size="small" customInput={TextField}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }} decimalScale={2} value={params.node.data.newVendorPrice ?? (params.node.data.vendor.vendorPrice ?? 0)}
                onKeyDown={(e) => {
                  if (e.key === '-') e.preventDefault();
                }}
                onValueChange={(value) => {
                  if (Number(value) !== params.node.data.vendor.vendorPrice && !Number.isNaN(Number(value))) {
 
                    setIsChanges(prev => ({ ...prev, [params.node.data.purchaseOrderNumber]: true }));
                    updateLocally(params, "newVendorPrice", !value ? 0 : value);
                  }
                }}
                onBlur={() => {
                  if (params.node.data.newVendorPrice !== undefined) {
                    updateField(params, "vendor.vendorPrice", params.node.data.newVendorPrice ?? 0);
                  }
                }}
              />
              </>
              : <>
                {(params.node.data.vendor.vendorPrice !== params.node.data.vendor.originalVendorPrice) ?
                  <Tooltip title={`Original value: ${(params.data.vendor.originalVendorPrice ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`} arrow placement="top">
                    <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0 }} onClick={() => {
                      updateLocally(params, "newVendorPrice", params.data.vendor.originalVendorPrice ? params.data.vendor.originalVendorPrice : 0);
                      updateField(params, "vendor.vendorPrice", params.node.data.newVendorPrice ?? 2);
                      setEditingRow((prev) => ({ ...prev, [params.data.id]: true }));
                      setIsChanges(prev => ({ ...prev, [params.node.data.purchaseOrderNumber]: true }));
                    }}>
                      <RestoreIcon />
                    </IconButton>
                  </Tooltip> : ""}
                <span>${Number(params.node.data.vendor.vendorPrice ?? 0).toFixed(2)}</span>
              </>}</Stack>)
      }
    ] : []),
    {
      field: 'price',
      headerName: 'Price',
      minWidth: 160,
      cellRenderer: (params) => (
        <Stack direction="row" spacing={1} alignItems="center"> {
          editingRow[params.data.id] && canEdit
            ? <> <CurrencyInput sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, width: "100px" }} InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              decimalScale={2} name="price" size="small" customInput={TextField} value={params.node.data.newPrice ?? (params.node.data.price ?? 0)}
              onKeyDown={(e) => {
                if (e.key === '-') e.preventDefault();
              }}
              onValueChange={(value) => {
                if (Number(value) !== params.node.data.price && !Number.isNaN(Number(value))) {
                  setIsChanges(prev => ({ ...prev, [params.node.data.purchaseOrderNumber]: true }));
                  updateLocally(params, "newPrice", !value ? 0 : value);
                }
              }}
              onBlur={() => {
                if (params.node.data.newPrice !== undefined) {
                  updateField(params, "price", params.node.data.newPrice ?? 0);
                }
              }}
            />
            </> : <>
              {(params.node.data.price !== params.node.data.originalPrice) ?
                <Tooltip title={`Original value: ${(params.data.originalPrice ?? 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}`} arrow placement="top">
                  <IconButton edge="end" color="secondary" disableRipple sx={{ px: 0 }} onClick={() => {
                    updateLocally(params, "newPrice", params.data.originalPrice ? params.data.originalPrice : 0);
                    updateField(params, "price", params.node.data.newPrice ?? 0);
                    setEditingRow((prev) => ({ ...prev, [params.data.id]: true }));
                    setIsChanges(prev => ({ ...prev, [params.node.data.purchaseOrderNumber]: true }));
                  }}>
                    <RestoreIcon />
                  </IconButton>
                </Tooltip> : ""}
              <span>${Number(params.node.data.price ?? 0).toFixed(2)}</span>
            </>}</Stack>)
    },
    {
      field: 'quantity',
      headerName: 'Quantity',
      minWidth: 160,
      cellRenderer: (params) => (
        <Stack direction="row" spacing={1} alignItems="center"> {editingRow[params.data.id]
          ? <> <TextField sx={{ "& .MuiOutlinedInput-root": { height: "34px" }, width: "100px" }} size="small" type="text" customInput={TextField}
            value={params.node.data.newQuantity ?? (params.node.data.quantity ?? 1)}
            onKeyDown={(e) => {
              const value = e.target.value ?? "";
              const selectionStart = e.target.selectionStart;
              const selectionEnd = e.target.selectionEnd;
              if (e.key === "0" && (value.length === 0 || (selectionStart === 0 && selectionEnd === value.length)))
                e.preventDefault();

              if (!/^[0-9]$/.test(e.key) && e.key !== "Backspace" && e.key !== "ArrowLeft" && e.key !== "ArrowRight") e.preventDefault();
            }}
            onChange={(e) => {
              let value = e.target.value;
              if (value === "") value = "";
              else value = Number(value);
              setIsChanges(prev => ({ ...prev, [params.node.data.purchaseOrderNumber]: true }));
              updateLocally(params, "newQuantity", value);
            }}
            onBlur={() => {
              if (params.node.data.newQuantity === "") updateLocally(params, "newQuantity", 1);
              if (params.node.data.newQuantity !== undefined) {
                const newQty = params.node.data.newQuantity;
                updateField(params, "quantity", newQty > 0 ? newQty : 1);
              }
            }}
          />
          </> : <>
            <span>{params.data.quantity ?? '1'}</span>
          </>}</Stack>)
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 100,
      cellRenderer: (params) => (
        editingRow?.[params.data.id] ? (
          <Tooltip arrow placement="top" title={
            params.node.data.newPrice || params.node.data.newQuantity || params.node.data.newVendorPrice ||
              (pendingEdits?.[params.data.purchaseOrderNumber]?.some(item => item.id === params.data.id))
              ? '' : 'Undo'} >
            <span> <IconButton onClick={() => {
              setEditingRow((prev) => { const updated = { ...prev }; delete updated[params.data.id]; return updated; })
              setIsChanges(prev => { const updated = { ...prev }; delete updated[params.data.purchaseOrderNumber]; return updated; });
            }}
              disabled={params.node.data.newPrice != null || params.node.data.newQuantity != null || params.node.data.newVendorPrice != null
                || (pendingEdits?.[params.data.purchaseOrderNumber]?.some(item => item.id === params.data.id))}>
              <Iconify icon="mdi:undo" />
            </IconButton>
            </span>
          </Tooltip >
        ) : (
          <Stack direction="row" spacing={1}>
            {!editingRow?.[params.data.id] && (
              <EditIconButton tooltipText="Edit pricing and quantity"
                disabled={startDeletingItems}
                onClick={() => {
                  const cleanRow = { ...params.data };
                  delete cleanRow.newPrice;
                  delete cleanRow.newQuantity;
                  delete cleanRow.newVendorPrice;
                  params.node.setData(cleanRow);
                  setEditingRow((prev) => ({ ...prev, [params.data.id]: true }));
                  setIsChanges(prev => ({ ...prev, [params.node.data.purchaseOrderNumber]: true }));
                }} />)}
          </Stack>))
    },
    ...(startDeletingItems ? [AGGridDeleteCol(setProducts)] : [])
  ], [startDeletingItems, setProducts, canEdit, showVendorCols, editingRow]);

  return (<>
    <Helmet>
      <title>Cart | NHIDirect</title>
    </Helmet>

    <Box sx={{ mt: 2, pb: 6 }}>
      <AGGridQuickFilterWrapper gridRefs={Object.values(gridRefs.current)}>
        <Box sx={{ width: { xs: '100%', md: 300 } }}>
          <Typography variant="subtitle1">Facility</Typography>
          <Autocomplete
            fullWidth
            size="small"
            value={selectedFacility ?? null}
            onChange={(event, val) => {
              setSelectedFacility(val);
              setEditingRow({});
              setPendingEdits({});
              setModifiedFieldsMap({});
              setIsChanges({});
              fetchData(val?.value);
            }}
            options={facilities}
            renderInput={(params) => <TextField {...params} placeholder="Facility" />}
          />
        </Box>
        <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>

          <Tooltip title={Object.keys(pendingEdits).length > 0 || Object.values(isChanges).some(value => value === true) ? "Unsaved Changes" : ""} arrow placement="top">
            <span>
              <LoadingButton variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} disabled={!formData?.some(product => product) || Object.keys(pendingEdits).length > 0 || Object.values(isChanges).some(value => value === true)}
                sx={{ marginTop: "auto" }} onClick={() => setStartSubmittingOrder(true)} color="primary" size="small">
                <Typography>Submit all carts{formData.length ? ` (${formData.reduce((acc, prod) => acc + prod.quantity * prod.price, 0)
                  .toLocaleString('en-US', { style: 'currency', currency: 'USD' })})` : ""}</Typography>
              </LoadingButton>
            </span>
          </Tooltip>
        </Box>
        <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>
          <Tooltip title={Object.keys(pendingEdits).length > 0 || Object.values(isChanges).some(value => value === true) ? "Unsaved Changes" : ""} arrow placement="top">
            <span>
              <DeleteStartButton disabled={Object.keys(pendingEdits).length > 0 || Object.values(isChanges).some(value => value === true)} setStartDeletingItems={setStartDeletingItems} startDeletingItems={startDeletingItems} setData={setProducts} />
            </span>
          </Tooltip>
        </Box>
        {startDeletingItems && <Box sx={{ width: { xs: '100%' }, marginTop: "auto" }}>
          <DeleteSubmitButton setStartDeletingItems={setStartDeletingItems} setData={setProducts}
            data={products} url="/cart/deleteCartItems" afterDelete={(data) => setFormData(data.map(item => ({ quantity: item.quantity, price: item.price, id: item.id, purchaseOrderNumber: item.purchaseOrderNumber, vendor: item.vendor })))} />
        </Box>}
      </AGGridQuickFilterWrapper>
      {!loading ? <Box sx={{ mt: 3 }}>
        {Object.entries(groupedProducts).map(([poNumber, items]) => (
          <Box key={poNumber} sx={{ mt: 4, display: visibleGrids[poNumber] === false ? 'none' : 'block' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: 2, marginBottom: 2 }}>

              <Typography variant="h6">PO #: {poNumber}</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>

                {(pendingEdits[poNumber]?.length > 0 || isChanges[poNumber]) && (
                  <>
                    <Button sx={{ px: 7 }} color="purple" size="medium" variant="contained" onClick={() => {
                      updateCart(poNumber);
                      const gridApi = gridRefs.current[poNumber]?.current?.api;
                      if (!gridApi) return;
                      gridApi.forEachNode((node) => {
                        if (node.data.purchaseOrderNumber === poNumber) {
                          const newData = { ...node.data };
                          delete newData.newPrice;
                          delete newData.newQuantity;
                          delete newData.newVendorPrice;
                          node.setData(newData);
                        }
                      });
                      setEditingRow((prev) => {
                        const updated = { ...prev };
                        products.filter(item => item.purchaseOrderNumber === poNumber).forEach(item => delete updated[item.id]); return updated;
                      });
                      setPendingEdits((prev) => { const copy = { ...prev }; delete copy[poNumber]; return copy; });
                      setIsChanges(prev => { const updated = { ...prev }; delete updated[poNumber]; return updated; });

                    }}>
                      Save
                    </Button>
                    <Button sx={{ px: 6 }} color="error" size="medium" variant="outlined"
                      onClick={() => {
                        setProducts(prev => prev.map(item => {
                          if (pendingEdits[poNumber]?.some(p => p.id === item.id)) {
                            const original = formData.find(f => f.id === item.id);
                            return { ...original };
                          }
                          return item;
                        }));
                        const gridApi = gridRefs.current[poNumber]?.current?.api;
                        if (gridApi) {
                          gridApi.forEachNode((node) => {
                            if (node.data.purchaseOrderNumber === poNumber) {
                              const cleanOriginal = formData.find(f => f.id === node.data.id);
                              if (cleanOriginal) {
                                const newData = { ...cleanOriginal };
                                delete newData.newPrice;
                                delete newData.newQuantity;
                                delete newData.newVendorPrice;
                                node.setData(newData);
                              }
                            }
                          });
                        }
                        setPendingEdits(prev => { const copy = { ...prev }; delete copy[poNumber]; return copy; });
                        setEditingRow(prev => {
                          const updated = { ...prev };
                          products.filter(item => item.purchaseOrderNumber === poNumber).forEach(item => delete updated[item.id]); return updated;
                        });
                        setModifiedFieldsMap(prev => {
                          const updated = { ...prev };
                          products.filter(item => item.purchaseOrderNumber === poNumber).forEach(item => delete updated[item.id]); return updated;
                        });
                        setIsChanges(prev => { const updated = { ...prev }; delete updated[poNumber]; return updated; });
                      }}>
                      Cancel
                    </Button>
                  </>
                )}
                <Tooltip title={Object.keys(pendingEdits).length > 0 || isChanges[poNumber] ? "Unsaved Changes" : ""} arrow placement="top">
                  <span>
                    <LoadingButton variant="contained" startIcon={<Iconify icon="eva:plus-fill" />} disabled={!items?.some(product => product) || isChanges[poNumber] || pendingEdits[poNumber]?.length > 0} color="primary" size="small"
                      onClick={() => { setStartSubmittingOrder(true); setFormDataPerCart(items.map(item => ({ quantity: item.quantity, price: item.price, id: item.id, purchaseOrderNumber: item.purchaseOrderNumber, vendor: item.vendor }))) }}>
                      <Typography> Submit Order{' '} {items.length ? ` (${items.reduce((acc, prod) => acc + prod.quantity, 0)} - 
                    ${items.reduce((acc, prod) => acc + prod.quantity * prod.price, 0).toLocaleString('en-US', { style: 'currency', currency: 'USD' })})` : ''}
                      </Typography>
                    </LoadingButton>
                  </span>
                </Tooltip>
                <Tooltip title={Object.keys(pendingEdits).length > 0 || isChanges[poNumber] ? "Unsaved Changes" : "Delete Cart"} arrow placement="top">
                  <span>
                    <IconButton color="error" disableRipple sx={{ px: 0 }} disabled={Object.keys(pendingEdits).length > 0 || isChanges[poNumber]} onClick={() => deleteCart(poNumber)} >
                      <DeleteOutline />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
            <Box>
              <AgGridReact
                ref={gridRefs.current[poNumber]}
                {...AgGridDefaults}
                columnDefs={columns}
                rowData={items} loading={loading}
                domLayout="autoHeight"
                onNewColumnsLoaded={autoSizeColumns}
                onFilterChanged={(params) => { params.api.refreshHeader(); handleFilterChanged(poNumber, params.api); }}
                getRowId={(params) => params.data.id}
                deltaRowDataMode
              />
            </Box>
          </Box>
        ))}
      </Box> : <Box sx={{ display: "flex", justifyContent: "center", m: 3 }}><CircularProgress /></Box>}
      <SubmitOrderSummary facilityAddress={facilityAddress} selectedFacility={selectedFacility} formData={formDataPerCart?.length ? formDataPerCart : formData} isAllCarts={!(formDataPerCart?.length > 0)}
        open={startSubmittingOrder}
        close={() => { setStartSubmittingOrder(false); setFormDataPerCart([]) }} />
    </Box>
  </>);
}
